
import { useState } from 'react';
import { 
  registerDomain,
  verifyDomain
} from '@/utils/licenseService';
import { DomainRegistration, LicenseKey } from '@/types/licenseKey';
import { useToast } from '@/hooks/use-toast';

export const useDomains = (
  domains: DomainRegistration[],
  setDomains: React.Dispatch<React.SetStateAction<DomainRegistration[]>>,
  licenseKeys: LicenseKey[],
  setLicenseKeys: React.Dispatch<React.SetStateAction<LicenseKey[]>>
) => {
  const { toast } = useToast();
  const [isRegisteringDomain, setIsRegisteringDomain] = useState(false);
  
  // Handle domain registration
  const handleRegisterDomain = async (data: {
    domain: string;
    licenseKeyId: string;
  }) => {
    setIsRegisteringDomain(true);
    try {
      const newDomain = await registerDomain(data);
      setDomains(prev => [newDomain, ...prev]);
      
      // Update license keys with new domain
      setLicenseKeys(prev => prev.map(key => 
        key.id === data.licenseKeyId ? { ...key, domain: data.domain } : key
      ));
      
      toast({
        title: "Domain Registered",
        description: `Successfully registered domain: ${newDomain.domain}`,
      });
      
      return newDomain;
    } catch (error) {
      console.error('Error registering domain:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to register domain",
        variant: "destructive",
      });
      throw error;
    } finally {
      setIsRegisteringDomain(false);
    }
  };
  
  // Handle domain verification
  const handleVerifyDomain = async (domainId: string) => {
    try {
      const verifiedDomain = await verifyDomain(domainId);
      
      setDomains(prev => prev.map(domain => 
        domain.id === domainId ? verifiedDomain : domain
      ));
      
      toast({
        title: "Domain Verified",
        description: `Successfully verified domain: ${verifiedDomain.domain}`,
      });
      
      return verifiedDomain;
    } catch (error) {
      console.error('Error verifying domain:', error);
      toast({
        title: "Error",
        description: "Failed to verify domain",
        variant: "destructive",
      });
      throw error;
    }
  };
  
  return {
    isRegisteringDomain,
    handleRegisterDomain,
    handleVerifyDomain
  };
};
